import React, { memo } from "react";
import cns from "classnames";
import { Card, Grid, makeStyles } from "@material-ui/core";
import { TodoPriority, TodoSearchItemFragment } from "@graphql";
import { AnyLocation } from "@navigation";
import { NodeRefListInline } from "@containers/NodeRefList/NodeRefList";
import { getTicketDetails } from "./getTicketDetails";

type Props = {
  item: TodoSearchItemFragment;
  onOpen: (e: React.SyntheticEvent) => void;
  navigateTo: (name: string, initialLocation: AnyLocation, baseUri?: string) => void;
  withAssignedBy: boolean;
  withAssignedTo: boolean;
};

function TicketCard(props: Props): React.ReactElement<Props> {
  const styles = useStyles({});
  const {
    item: { title, priority, relatedTo },
    onOpen
  } = props;

  const getTicketBackground = () => {
    if (priority === TodoPriority.High) {
      return "#fffdd9";
    }
    return "";
  };

  const details = getTicketDetails(props.item);

  if (!props.withAssignedBy) {
    details.remove((x) => x.key === "assignedBy");
  }

  if (!props.withAssignedTo) {
    details.remove((x) => x.key === "assignedTo");
  }

  return (
    <Card
      className={cns("hl", styles.root)}
      style={{
        backgroundColor: getTicketBackground()
      }}
      elevation={2}
      onClick={onOpen}
    >
      <div>
        <div className={styles.cardTitleWrap}>
          <span className={styles.cardTitle}>{title}</span>{" "}
          {relatedTo && (
            <NodeRefListInline items={relatedTo} onClick={(e) => e.stopPropagation()} />
          )}
        </div>

        <div className={styles.details}>
          {details.length > 0 && (
            <Grid container className={styles.detailItems}>
              {details.map((detail) => {
                const icon = React.createElement<any>(detail.icon, {
                  className: styles.icon
                });

                return (
                  <Grid
                    key={detail.key}
                    item
                    xs={12}
                    md={6}
                    xl={4}
                    className={styles.withIconWrap}
                  >
                    {icon}
                    <span>
                      {detail.title}: {detail.label}
                    </span>
                  </Grid>
                );
              })}
            </Grid>
          )}

        </div>
      </div>
    </Card>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    marginRight: "7px",
    borderRadius: 2,
    color: "#202020",
    cursor: "pointer",
    "&:hover, &:active": {
      boxShadow: theme.shadows[4]
    },
    overflow: "unset",
    padding: 0
  },
  cardTitle: {
    fontWeight: "bold",
    fontSize: "16px",
    width: "100%"
  },
  cardTitleWrap: {
    padding: 16,
    borderBottom: `1px solid ${theme.palette.divider}`
  },

  details: {
    padding: 16,
    display: "flex"
  },

  detailItems: {
    flex: 1,
    display: "Flex"
  },

  cardAssignedBy: {
    fontSize: "14px",
    color: "#25a69a"
  },
  cardAssignedByIcon: {
    verticalAlign: "bottom"
  },
  withIconWrap: {
    display: "flex",
    alignItems: "center",
    marginBottom: 4
  },
  icon: {
    fontSize: 14,
    color: "#616161",
    width: 20
  }
}));

export default memo(TicketCard);
